import { createJsonClient } from '../../../shared/httpClient';
import type {
  AuthorizedPartner,
  InvitationPreview,
  InviteClientRequest,
  EnterClientSummary,
  PartnerAssignment,
  PartnerClient,
  PartnerClientInvitationResult,
  PartnerHistoryEntry,
  PartnerMeResponse,
  PartnerOrganization,
  PartnerRole,
  PartnerTeamMember,
} from '../types';
import type { CompanyPartnersGateway, PartnerGateway } from './PartnerGateway';

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const put = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });

export class HttpPartnerGateway implements PartnerGateway {
  private readonly request = createJsonClient('');

  getMe(signal?: AbortSignal) {
    return this.request<PartnerMeResponse>('/api/partner/me', undefined, signal);
  }

  getOrganization(organizationId: string, signal?: AbortSignal) {
    return this.request<PartnerOrganization>(`/api/partner/${organizationId}`, undefined, signal);
  }

  acceptAgreement(organizationId: string, version: string) {
    return this.request<PartnerOrganization>(`/api/partner/${organizationId}/agreement/accept`, json({ version }));
  }

  listClients(organizationId: string, signal?: AbortSignal) {
    return this.request<PartnerClient[]>(`/api/partner/${organizationId}/clients`, undefined, signal);
  }

  getClient(organizationId: string, relationshipId: string, signal?: AbortSignal) {
    return this.request<PartnerClient>(`/api/partner/${organizationId}/clients/${relationshipId}`, undefined, signal);
  }

  inviteClient(organizationId: string, request: InviteClientRequest) {
    return this.request<PartnerClientInvitationResult>(`/api/partner/${organizationId}/clients/invitations`, json(request));
  }

  resendInvitation(organizationId: string, relationshipId: string) {
    return this.request<PartnerClientInvitationResult>(`/api/partner/${organizationId}/clients/${relationshipId}/invitation/resend`, { method: 'POST' });
  }

  endRelationship(organizationId: string, relationshipId: string, reason: string) {
    return this.request<PartnerClient>(`/api/partner/${organizationId}/clients/${relationshipId}/end`, json({ reason }));
  }

  listAssignments(organizationId: string, relationshipId: string, signal?: AbortSignal) {
    return this.request<PartnerAssignment[]>(`/api/partner/${organizationId}/clients/${relationshipId}/assignments`, undefined, signal);
  }

  assign(organizationId: string, relationshipId: string, membershipId: string) {
    return this.request<PartnerAssignment>(`/api/partner/${organizationId}/clients/${relationshipId}/assignments`, json({ membershipId }));
  }

  async unassign(organizationId: string, relationshipId: string, assignmentId: string) {
    await this.request<void>(`/api/partner/${organizationId}/clients/${relationshipId}/assignments/${assignmentId}`, { method: 'DELETE' });
  }

  clientHistory(organizationId: string, relationshipId: string, signal?: AbortSignal) {
    return this.request<PartnerHistoryEntry[]>(`/api/partner/${organizationId}/clients/${relationshipId}/history`, undefined, signal);
  }

  enterClient(organizationId: string, relationshipId: string) {
    return this.request<EnterClientSummary>(`/api/partner/${organizationId}/clients/${relationshipId}/enter`, { method: 'POST' });
  }

  async exitClient() {
    await this.request<void>('/api/me/exit-client', { method: 'POST' });
  }

  listTeam(organizationId: string, signal?: AbortSignal) {
    return this.request<PartnerTeamMember[]>(`/api/partner/${organizationId}/team`, undefined, signal);
  }

  changeRole(organizationId: string, membershipId: string, role: PartnerRole) {
    return this.request<PartnerTeamMember>(`/api/partner/${organizationId}/team/${membershipId}/role`, put({ role }));
  }

  async deactivateMember(organizationId: string, membershipId: string, reason?: string) {
    await this.request<void>(`/api/partner/${organizationId}/team/${membershipId}`, { method: 'DELETE', ...(reason ? { body: JSON.stringify({ reason }) } : {}) });
  }
}

export class HttpCompanyPartnersGateway implements CompanyPartnersGateway {
  private readonly request = createJsonClient('');
  private static readonly base = '/api/company/authorized-partners';

  list(signal?: AbortSignal) {
    return this.request<AuthorizedPartner[]>(HttpCompanyPartnersGateway.base, undefined, signal);
  }

  previewInvitation(token: string) {
    return this.request<InvitationPreview>(`${HttpCompanyPartnersGateway.base}/invitation/preview`, json({ token }));
  }

  acceptInvitation(token: string, services: string[]) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/invitation/accept`, json({ token, services }));
  }

  async declineInvitation(token: string) {
    await this.request<void>(`${HttpCompanyPartnersGateway.base}/invitation/decline`, json({ token }));
  }

  reduceServices(relationshipId: string, services: string[]) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/services`, put({ services }));
  }

  setExpiry(relationshipId: string, expiresAtUtc: string | null) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/expiry`, put({ expiresAtUtc }));
  }

  activate(relationshipId: string) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/activate`, { method: 'POST' });
  }

  suspend(relationshipId: string, reason: string) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/suspend`, json({ reason }));
  }

  resume(relationshipId: string) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/resume`, { method: 'POST' });
  }

  revoke(relationshipId: string, reason: string) {
    return this.request<AuthorizedPartner>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/revoke`, json({ reason }));
  }

  history(relationshipId: string, signal?: AbortSignal) {
    return this.request<PartnerHistoryEntry[]>(`${HttpCompanyPartnersGateway.base}/${relationshipId}/history`, undefined, signal);
  }
}
